import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import {
  Monitor, DollarSign, Heart, Newspaper, Briefcase, GraduationCap,
  Plane, Trophy, Film, Home, Scale, Shirt, ShoppingCart, Utensils,
  Gamepad2, Car
} from 'lucide-react';

const niches = [
  { name: 'Technology', icon: Monitor, sites: '42,300', avgPrice: '$180 - $2,500', query: 'technology' },
  { name: 'Finance', icon: DollarSign, sites: '18,900', avgPrice: '$250 - $5,000', query: 'finance' },
  { name: 'Health', icon: Heart, sites: '31,200', avgPrice: '$120 - $3,200', query: 'health' },
  { name: 'News & Media', icon: Newspaper, sites: '15,600', avgPrice: '$200 - $8,000', query: 'news' },
  { name: 'Business', icon: Briefcase, sites: '28,400', avgPrice: '$150 - $4,500', query: 'business' },
  { name: 'Education', icon: GraduationCap, sites: '22,100', avgPrice: '$80 - $1,800', query: 'education' },
  { name: 'Travel', icon: Plane, sites: '19,800', avgPrice: '$100 - $2,200', query: 'travel' },
  { name: 'Sports', icon: Trophy, sites: '14,300', avgPrice: '$90 - $1,500', query: 'sports' },
  { name: 'Entertainment', icon: Film, sites: '16,700', avgPrice: '$80 - $2,000', query: 'entertainment' },
  { name: 'Real Estate', icon: Home, sites: '8,900', avgPrice: '$220 - $3,800', query: 'real estate' },
  { name: 'Legal', icon: Scale, sites: '6,200', avgPrice: '$300 - $5,500', query: 'legal' },
  { name: 'Fashion', icon: Shirt, sites: '12,400', avgPrice: '$70 - $1,200', query: 'fashion' },
  { name: 'E-commerce', icon: ShoppingCart, sites: '21,600', avgPrice: '$130 - $2,800', query: 'ecommerce' },
  { name: 'Food & Drink', icon: Utensils, sites: '9,800', avgPrice: '$60 - $900', query: 'food' },
  { name: 'Gaming', icon: Gamepad2, sites: '11,500', avgPrice: '$80 - $1,600', query: 'gaming' },
  { name: 'Automotive', icon: Car, sites: '7,300', avgPrice: '$120 - $2,200', query: 'automotive' },
];

export default function BrowseNiche() {
  const navigate = useNavigate();

  const handleNicheClick = (query: string) => {
    navigate(`/?q=${encodeURIComponent(query)}&mode=niche`);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Browse Guest Post Sites by Niche  -  Technology, Finance, Health & More"
        description="Explore guest posting sites by category. Browse 490,000+ domains across 16 niches with real pricing data from 50+ guest post marketplaces. Find guest posting opportunities in your niche."
        canonical="/browse"
      />
      <TopNav />

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Browse Guest Posting Sites by Niche</h1>
          <p className="text-lg text-slate-500">Explore guest post marketplace listings by category. Find guest posting opportunities with real prices.</p>
        </div>

        {/* Niche Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {niches.map(niche => {
            const Icon = niche.icon;
            return (
              <button
                key={niche.name}
                onClick={() => handleNicheClick(niche.query)}
                className="text-left p-5 border border-slate-200 rounded-2xl bg-white shadow-card hover:shadow-card-hover hover:border-brand-400 transition-all group"
              >
                <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center mb-3 group-hover:bg-brand-100 transition-colors">
                  <Icon className="w-5 h-5 text-brand-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{niche.name}</h3>
                <p className="text-xs text-slate-500 mb-1">{niche.sites} sites</p>
                <p className="text-xs text-brand-600 font-medium">{niche.avgPrice}</p>
              </button>
            );
          })}
        </div>

        {/* Popular Domains */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Popular Domains</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
            {[
              { domain: 'forbes.com', price: '$5,920', quality: 94 },
              { domain: 'techcrunch.com', price: '$1,200', quality: 72 },
              { domain: 'entrepreneur.com', price: '$450', quality: 86 },
              { domain: 'hubspot.com', price: '$321', quality: 93 },
              { domain: 'mashable.com', price: '$403', quality: 92 },
              { domain: 'neilpatel.com', price: '$264', quality: 91 },
              { domain: 'moz.com', price: '$380', quality: 91 },
              { domain: 'ahrefs.com', price: '$520', quality: 92 },
            ].map(d => (
              <button key={d.domain} onClick={() => navigate(`/domain/${d.domain}`)} className="flex-shrink-0 w-48 p-4 border border-slate-200 rounded-xl hover:border-brand-400 hover:shadow-sm transition-all text-left">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 uppercase mb-2">{d.domain.charAt(0)}</div>
                <p className="font-semibold text-slate-900 text-sm truncate">{d.domain}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-bold text-brand-600">{d.price}</span>
                  <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold">{d.quality}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
